import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Clock,
  Eye,
  ExternalLink,
  Loader2,
  Mail,
  RefreshCw,
  Users,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
  Send,
  Search,
  MapPin,
  Copy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract postcode from address for Google search
const extractPostcode = (address) => {
  if (!address) return '';
  const match = address.match(/\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/i);
  return match ? match[1].toUpperCase() : '';
};

// Build Google search URL for finding business contact/Facebook
const buildGoogleSearchUrl = (businessName, address) => {
  const postcode = extractPostcode(address);
  const searchQuery = postcode 
    ? `${businessName} ${postcode}`
    : businessName;
  return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
};

// Format address with proper capitalization
const formatAddress = (address) => {
  if (!address) return '';
  return address
    .replace(/\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/gi, match => match.toUpperCase())
    .split(' ')
    .map(word => word.length <= 4 && word === word.toUpperCase() ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Extract area/town from address for FB message
const extractArea = (address) => {
  if (!address) return 'the area';
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    const townPart = parts[parts.length - 2] || parts[0];
    return townPart.replace(/\b[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}\b/gi, '').trim() || 'the area';
  }
  return 'the area';
};

// Generate DM for social media outreach
const generateDM = () => {
  return `Hey! I figured I would drop a message on socials as I know lots of people think emails are spam which is very fair haha! I build websites for independent companies and they're just on basic subscription models to keep the price down - let me know if you'd like a preview link! I can make adjustments too 😊`;
};

const NoResponse = () => {
  const { token } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API}/websites/no-response`, { headers });
      setWebsites(response.data);
    } catch (error) {
      console.error('Failed to fetch no response websites:', error);
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWebsites();
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWebsites();
  };

  const handleRetry = async (websiteId) => {
    setRetrying(prev => ({ ...prev, [websiteId]: true }));
    try {
      await axios.post(`${API}/websites/${websiteId}/retry-preview`, {}, { headers });
      toast.success('Moved back to Preview Sent');
      fetchWebsites();
    } catch (error) {
      console.error('Failed to retry preview:', error);
      toast.error('Failed to retry');
    } finally {
      setRetrying(prev => ({ ...prev, [websiteId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysSince = (dateStr) => {
    if (!dateStr) return 0;
    const date = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <AlertCircle className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <CardTitle>No Response ({websites.length})</CardTitle>
                <CardDescription>
                  Previews sent over 7 days ago with no client action
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Websites List */}
      {websites.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
            <p className="text-lg font-medium">All caught up!</p>
            <p className="text-sm text-muted-foreground">
              No previews are waiting for a response longer than 7 days
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {websites.map((website) => {
            const daysSinceSent = getDaysSince(website.updated_at);
            const hasViewed = website.analytics?.total_views > 0;
            
            return (
              <Card key={website.id} data-testid={`website-${website.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{website.business_name}</h3>
                        <Badge variant="outline" className="border-yellow-500 text-yellow-500">
                          {daysSinceSent} days ago
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        {/* Address if available */}
                        {website.formatted_address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="w-3 h-3" />
                            {formatAddress(website.formatted_address)}
                          </p>
                        )}
                        {/* Google Search link */}
                        <a 
                          href={buildGoogleSearchUrl(website.business_name, website.formatted_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-400 hover:underline"
                        >
                          <Search className="w-3 h-3" />
                          Search on Google
                        </a>
                        {/* Copy DM */}
                        <button
                          onClick={() => {
                            const message = generateDM();
                            navigator.clipboard.writeText(message).then(() => {
                              toast.success('DM copied to clipboard!');
                            }).catch(() => {
                              toast.error('Failed to copy message');
                            });
                          }}
                          className="flex items-center gap-2 text-blue-400 hover:underline text-left"
                        >
                          <Copy className="w-3 h-3" />
                          Copy DM
                        </button>
                        <a 
                          href={`https://tryweb.co.uk/preview/${website.subdomain}?_admin=1`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          tryweb.co.uk/preview/{website.subdomain}
                        </a>
                        
                        <p className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          Sent to: <span className="text-purple-400">{website.client_email || 'Unknown'}</span>
                        </p>
                        
                        <p className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          Preview sent: {formatDate(website.updated_at)}
                        </p>
                        
                        {/* Analytics */}
                        {website.analytics && (
                          <div className="flex items-center gap-3 pt-2 mt-2 border-t border-white/5">
                            {hasViewed ? (
                              <>
                                <Badge variant="outline" className="border-green-500 text-green-400 gap-1.5">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Viewed
                                </Badge>
                                <span className="flex items-center gap-1.5 text-xs">
                                  <Eye className="w-3.5 h-3.5 text-blue-400" />
                                  <span className="font-medium text-foreground">{website.analytics.total_views}</span> views
                                </span>
                                <span className="flex items-center gap-1.5 text-xs">
                                  <Users className="w-3.5 h-3.5 text-green-400" />
                                  <span className="font-medium text-foreground">{website.analytics.unique_visitors}</span> unique
                                </span>
                              </>
                            ) : (
                              <Badge variant="outline" className="border-red-500 text-red-400 gap-1.5">
                                <AlertCircle className="w-3 h-3" />
                                Never viewed
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`https://tryweb.co.uk/preview/${website.subdomain}?_admin=1`, '_blank')}
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </Button>
                      
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleRetry(website.id)}
                        disabled={retrying[website.id]}
                        className="gap-2"
                        data-testid={`retry-${website.id}`}
                      >
                        {retrying[website.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4" />
                        )}
                        Retry
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tips */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg">Follow-up Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Eye className="w-4 h-4 text-blue-500 mt-0.5" />
              Check if they viewed the preview - "Viewed" means they saw it but didn't respond
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-purple-500 mt-0.5" />
              "Never viewed" might mean the email went to spam - try a different approach
            </li>
            <li className="flex items-start gap-2">
              <RotateCcw className="w-4 h-4 text-green-500 mt-0.5" />
              Click "Retry" to move back to Preview Sent and send a follow-up email
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default NoResponse;
